import React, { forwardRef, useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Link from '@polestar/component-warehouse-react/components/link/Link';
import Icon, { IconSizes } from '../../icon/Icon';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { useFocusGroup } from '@polestar/component-warehouse-react/hooks/focus';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import Backdrop from '../../backdrop/Backdrop';
import { useHeader } from '../../header/HeaderContext';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import { MotionDurationsSeconds, MotionEasings, createCSSTransition, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import Text from '@polestar/component-warehouse-react/components/text/Text';
import { MegaMenuCSSCustomProperties } from '@polestar/component-warehouse-react/components/mega-menu/MegaMenu';
const SideNavigationDropdown = forwardRef(({ items, actions, onBlur, ...props }, ref) => {
    const [open, setOpen] = useState(false);
    const { headerVariant, subNavigationSafeAreaHeight } = useHeader();
    const [firstItemRef, firstItemNode] = useCallbackRef();
    const [dropdownLabelRef, dropdownLabelNode] = useCallbackRef();
    const [dropdownRef, dropdownNode] = useCallbackRef();
    // determine if MegaMenu or Header is used to determine what safeAreaHeight to use.
    const usesMegaMenu = headerVariant === null;
    // Find the active item for dropdown label
    const activeItem = items.find((item) => item.active === true);
    // for focus looping with arrows
    const focusRef = useRef(null);
    const combinedRef = useCombinedRef(ref, focusRef);
    const { createFocusProps } = useFocusGroup({
        ref: focusRef,
        initialFocusIndex: 0,
    });
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownLabelNode &&
                !dropdownLabelNode.contains(event.target) &&
                dropdownNode &&
                !dropdownNode.contains(event.target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownLabelNode, dropdownNode, open]);
    useEffect(() => {
        if (open) {
            firstItemNode === null || firstItemNode === void 0 ? void 0 : firstItemNode.focus();
        }
    }, [firstItemNode, open]);
    const handleClick = () => {
        setOpen(!open);
    };
    const handleClose = () => {
        setOpen(false);
        dropdownLabelNode === null || dropdownLabelNode === void 0 ? void 0 : dropdownLabelNode.focus();
    };
    return (React.createElement(StyledSideNavigationDropdown, null,
        React.createElement(Backdrop, { "data-type": "backdrop", visible: open, style: { height: '100vh', position: 'absolute' }, onClose: handleClose }),
        React.createElement(Wrapper, { ...props, ref: combinedRef, onBlur: onBlur, "data-type": "side-navigation-wrapper" },
            React.createElement(DropdownWrapper, null,
                React.createElement(DropdownLabel, { ref: dropdownLabelRef, onClick: handleClick, ...aria({ expanded: open, haspopup: 'menu' }), withSlotContent: actions !== undefined, "data-type": "side-navigation-menu-button" },
                    React.createElement(Text, { lineClamp: 1 }, activeItem ? activeItem.label : items[0].label),
                    React.createElement(StyledIcon, { icon: open ? Icons.chevronUp : Icons.chevronDown, pairedWithText: true, size: IconSizes.medium })),
                actions && React.createElement(Slot, { "data-type": "side-navigation-actions" }, actions)),
            React.createElement(Content, { key: "side-navigation-dropdown-content", open: open, safeAreaHeight: subNavigationSafeAreaHeight, usesMegaMenu: usesMegaMenu, role: "menu", "aria-hidden": !open, ref: dropdownRef }, items.map(({ label, href, active, onClick, onLinkClick, ...itemProps }, index) => {
                if (href.startsWith('#')) {
                    console.error(`SideNavigationDefault: Items should only be Links not anchors: ${label} starts with "#".`);
                }
                const handleClick = (e) => {
                    onClick === null || onClick === void 0 ? void 0 : onClick(e);
                    onLinkClick === null || onLinkClick === void 0 ? void 0 : onLinkClick(index);
                };
                return (React.createElement(LinkWrapper, { key: `side-navigation-link-${index}`, role: "menuitem" },
                    React.createElement(StyledLink, { "data-type": "side-navigation-link", href: href, ...createFocusProps(index), onClick: handleClick, selected: active, ...itemProps, ref: index === 0 ? firstItemRef : null, tabIndex: open ? 0 : -1 }, label)));
            })))));
});
SideNavigationDropdown.displayName = 'SideNavigationDropdown';
export default SideNavigationDropdown;
const StyledSideNavigationDropdown = styled.div(() => css `
    position: relative;

    ${responsiveStyles({
    display: {
        mobile: 'block',
        tablet: 'block',
        desktop: 'none',
    },
})};
  `);
const Wrapper = styled.nav `
  width: 100%;
  position: relative;
  z-index: ${LayoutIndexes.modal};
`;
const DropdownWrapper = styled.span(({ theme }) => css `
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${theme.sideNavigation.backgroundColor};
    text-align: start;
    position: relative;

    min-height: ${createCustomSpacing(56 / 16)};
    box-shadow: inset 0 -${createCustomSpacing(2 / 16)} ${createCustomSpacing(1 / 16)} -${createCustomSpacing(1 / 16)}
      ${theme.sideNavigation.border.color};
  `);
const DropdownLabel = styled.button(({ theme, withSlotContent }) => css(css `
      flex: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: none;
      background-color: inherit;
      text-align: start;
      gap: ${createCustomSpacing(6 / 16)};
      color: ${theme.sideNavigation.link.selectedColor};

      &:hover {
        color: ${theme.sideNavigation.link.hoverColor};
        cursor: pointer;
      }

      &:active {
        color: ${theme.sideNavigation.link.selectedColor};
      }

      &:focus {
        color: ${theme.sideNavigation.link.selectedColor};
      }

      ${responsiveStyles({
    padding: {
        mobile: `${Spacings.medium} 0`,
        tablet: `${Spacings.medium} 0`,
    },
    paddingInline: {
        mobile: `6vw`,
        tablet: `${Spacings.xxLarge}`,
    },
})};
    `, withSlotContent &&
    css `
        flex: initial;
        justify-content: flex-start;

        ${responsiveStyles({
        paddingInline: {
            mobile: `6vw ${createCustomSpacing(24 / 16)}`,
            tablet: `${Spacings.xxLarge} ${createCustomSpacing(24 / 16)}`,
        },
    })};
      `));
const StyledIcon = styled(Icon)(() => css `
    height: ${createCustomSpacing(16 / 16)};
    margin-top: ${createCustomSpacing(2 / 16)};
    transform-origin: center;
  `);
const Slot = styled.div(() => css `
    display: flex;
    flex-direction: row;
    align-items: center;

    ${responsiveStyles({
    padding: {
        mobile: 0,
        tablet: 0,
    },
    paddingInline: {
        mobile: `0 6vw `,
        tablet: `0 ${Spacings.xxLarge}`,
    },
})};
  `);
const Content = styled.ul(({ theme, open, safeAreaHeight, usesMegaMenu }) => css(css `
        position: absolute;
        left: 0;
        width: 100%;
        display: flex;
        flex-direction: column;
        background-color: ${theme.sideNavigation.backgroundColor};
        z-index: 1;

        opacity: 0;
        transform: translateY(-${createCustomSpacing(30 / 16)});
        transition: ${createCSSTransition('opacity', MotionEasings.linear, MotionDurationsSeconds.fast)},
          ${createCSSTransition('transform', MotionEasings.linear, MotionDurationsSeconds.normal)};
        pointer-events: none;

        max-height: calc(
          100vh -
            ${
/* MegaMenu uses a CSS Custom Property while Header uses the legacy JS definition */
!usesMegaMenu
    ? `${safeAreaHeight}px `
    : `var(${MegaMenuCSSCustomProperties.safeAreaHeight}) - ${
    /* this is the minimal space of the MegaMenu*/
    Spacings.xxLarge}`}
        );
        overflow-y: auto;

        ${responsiveStyles({
    padding: {
        mobile: `${Spacings.xSmall} 6vw`,
        tablet: `${Spacings.xSmall} ${Spacings.xxLarge}`,
    },
})};
      `, open &&
    css `
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        `));
const LinkWrapper = styled.li `
  ${() => css `
    display: flex;
    width: 100%;
  `}
`;
const StyledLink = styled(Link)(({ theme, selected }) => css(css `
      width: 100%;
      text-decoration: none;
      border-bottom: ${createCustomSpacing(1 / 16)} solid ${theme.sideNavigation.border.color};
      padding: ${createCustomSpacing(15 / 16)} 0;

      &:hover {
        text-decoration: none;
      }

      &:active {
        color: ${theme.sideNavigation.link.selectedColor};
      }
    `, selected &&
    css `
        color: ${theme.sideNavigation.link.selectedColor};
      `));
